import { useState, useCallback } from "react";
import { ProjectData } from "../../types";
import type { ViewState } from "react-map-gl/mapbox";

const defaultLocation = {
  latitude: 40.7128,
  longitude: -74.006,
  zoom: 12,
  bearing: 0,
  pitch: 0,
};

interface UseMapViewportLogicProps {
  projectData: ProjectData | null;
  setProjectData: (data: ProjectData | null) => void;
  lastSaveProject: ProjectData | null;
}

export const useMapViewportLogic = ({
  projectData,
  setProjectData,
  lastSaveProject,
}: UseMapViewportLogicProps) => {
  const [viewport, setViewport] = useState<Partial<ViewState>>(defaultLocation);
  // Add state to track if map is moving
  const [isMapMoving, setIsMapMoving] = useState(false);

  // Reset map to last saved position
  const handleResetMapPosition = useCallback(() => {
    if (!lastSaveProject?.location) return;

    // If map is currently moving, wait a bit for it to settle
    if (isMapMoving) {
      setTimeout(() => {
        setViewport(lastSaveProject?.location);
        if (projectData?.location) {
          const updatedProjectData: ProjectData = {
            ...projectData,
            location: {
              ...projectData.location,
            },
          };
          setProjectData(updatedProjectData);
        }
      }, 100); // Small delay to let current movement settle
    } else {
      // Map is not moving, reset immediately
      setViewport(lastSaveProject.location);
      if (projectData?.location) {
        const updatedProjectData: ProjectData = {
          ...projectData,
          location: {
            ...projectData.location,
          },
        };
        setProjectData(updatedProjectData);
      }
    }
  }, [projectData, isMapMoving]);

  // Update viewport when map moves (no location changes)
  const handleMapMove = useCallback((evt: any) => {
    setViewport(evt.viewState);
    setIsMapMoving(true);
  }, []);

  // Handle map movement start
  const handleMapMoveStart = useCallback(() => {
    setIsMapMoving(true);
  }, []);

  // Handle map movement end
  const handleMapMoveEnd = useCallback(() => {
    setIsMapMoving(false);
  }, []);

  return {
    viewport,
    setViewport,
    isMapMoving,
    setIsMapMoving,
    handleResetMapPosition,
    handleMapMove,
    handleMapMoveStart,
    handleMapMoveEnd,
  };
};
